import { ReactElement, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'

import { getCommentCategories } from '../../../redux/management-ts/managementActions'
import { Reducers } from '../../../redux/reducers'
import { LoadingSpinner } from '../../components'
import { UserDetail } from '../../containers'
import useCheckPermission from '../../helpers/hooks/useCheckPermission'
import trans from '../../helpers/trans'
import { fetchEmploymentTypes } from '../../redux/employmentTypes/employmentTypesActions'
import { getEventLogsByEntityUuid } from '../../redux/eventLogs/eventLogsActions'
import { fetchExperiences } from '../../redux/experiences/experiencesActions'
import { fetchRoles } from '../../redux/roles/rolesActions'
import { useTypedDispatch, useTypedSelector } from '../../redux/store'
import { setToast } from '../../redux/toasts/toastsReducer'
import { fetchTransportationOptions } from '../../redux/transportationOptions/transportationOptionsActions'
import {
    getLoketUser,
    getUserProfileV3,
    resendUserRegistration,
    resetUserPassword,
} from '../../redux/user/userActions'
import {
    setIsResendRegistrationSuccessful,
    setIsResetUserPasswordSuccessful,
    setLoketUser,
    setUserProfile,
} from '../../redux/user/userReducer'
import { ToastType } from '../../types'

const ConnectedUserDetail = (): ReactElement => {
    const dispatch = useTypedDispatch()
    const { userId } = useParams<{ userId: string }>()

    const loketError = useSelector<Reducers, any>(state => state.appReducer.loketError)

    const { isLoading: isEmploymentTypesLoading, employmentTypes } = useTypedSelector(state => state.employmentTypesReducer)
    const { isLoading: isExperiencesLoading, experiences } = useTypedSelector(state => state.experiencesReducer)
    const { isLoading: isTransportOptionsLoading, transportationOptions } = useTypedSelector(state => state.transportationOptionsReducer)
    const { isLoading: isRolesLoading, roles } = useTypedSelector(state => state.rolesReducer)
    const { user: currentUser, permissions } = useTypedSelector(state => state.authenticatedUserReducer)
    const {
        isLoading: isUserLoading,
        isUserProfileLoading,
        isLoketUserLoading,
        isResendRegistrationSuccessful,
        isResetUserPasswordSuccessful,
        userProfile,
        loketUser,
    } = useTypedSelector(state => state.pacoUserReducer)
    const { commentCategories } = useSelector((state: Reducers) => state.managementReducer)
    const { eventLogsByEntity: logs, isLoading: isLogsLoading } = useTypedSelector(state => state.eventLogsReducer)

    const canViewLoketData = useCheckPermission('manage-loket-data')

    const isUserFormDataLoading = isEmploymentTypesLoading || isExperiencesLoading || isTransportOptionsLoading || isRolesLoading
    const isLoading = isUserLoading || isUserProfileLoading

    const [logsModalOpen, setLogsModalOpen] = useState<boolean>(false)

    useEffect((): () => void => {
        if (userId) {
            dispatch(getUserProfileV3(userId))
        }

        return () => dispatch(setUserProfile(undefined))
    }, [userId])

    useEffect((): () => void => {
        if (canViewLoketData && userProfile?.loketEmployeeId) {
            dispatch(getLoketUser(userProfile.loketEmployeeId))
        }

        return () => dispatch(setLoketUser(undefined))
    }, [userProfile?.loketEmployeeId])

    useEffect((): void => {
        if (! employmentTypes.length) {
            dispatch(fetchEmploymentTypes())
        }

        if (! experiences.length) {
            dispatch(fetchExperiences())
        }

        if (! transportationOptions.length) {
            dispatch(fetchTransportationOptions())
        }

        if (! roles.length) {
            dispatch(fetchRoles())
        }

        if (! commentCategories.length) {
            dispatch(getCommentCategories())
        }
    }, [])

    useEffect((): void => {
        if (isResetUserPasswordSuccessful) {
            dispatch(setIsResetUserPasswordSuccessful(false))

            dispatch(setToast({
                text: trans('containers.forms.editUserForm.sendResetPasswordAndPinSuccess'),
                type: ToastType.pass,
            }))
        }
    }, [isResetUserPasswordSuccessful])

    useEffect((): void => {
        if (isResendRegistrationSuccessful) {
            dispatch(setIsResendRegistrationSuccessful(false))

            dispatch(setToast({
                text: trans('containers.forms.editUserForm.resendRegistrationMailSuccess'),
                type: ToastType.pass,
            }))
        }
    }, [isResendRegistrationSuccessful])

    useEffect((): void => {
        if (loketError) {
            dispatch(setToast({
                text: trans('errors.loketUnknownError'),
                type: ToastType.warning,
                shouldFadeOutAutomatically: false,
            }))
        }
    }, [loketError])

    const handleAfterEditUserSuccess = (): void => {
        if (userProfile) {
            dispatch(getUserProfileV3(userProfile.id))
        }
    }

    const handleResendRegistrationMailButtonClick = (): void => {
        if (userProfile) {
            dispatch(resendUserRegistration(userProfile.id))
        }
    }

    const handleSendResetPasswordAndPinButtonClick = (): void => {
        if (userProfile) {
            dispatch(resetUserPassword(userProfile.id))
        }
    }

    const handleHistoryButtonClick = (): void => {
        if (userProfile) {
            dispatch(getEventLogsByEntityUuid(userProfile.id))
            setLogsModalOpen(true)
        }
    }

    const handleLogsModalClose = (): void => {
        setLogsModalOpen(false)
    }

    if (! userProfile || ! currentUser) {
        return (
            <LoadingSpinner />
        )
    }

    return (
        <UserDetail
            isLoading={isLoading}
            isLoadingLogs={isLogsLoading}
            isLoadingUserFormData={isUserFormDataLoading}
            isLoadingLoketUser={isLoketUserLoading}
            isLogsModalOpen={logsModalOpen}
            currentUser={currentUser}
            employmentTypes={employmentTypes}
            experiences={experiences}
            transportationOptions={transportationOptions}
            logs={logs}
            permissions={permissions}
            roles={roles}
            user={userProfile}
            loketUser={loketUser}
            onAfterEditUserSuccess={handleAfterEditUserSuccess}
            onHistoryButtonClick={handleHistoryButtonClick}
            onLogsModalClose={handleLogsModalClose}
            onResendRegistrationMailButtonClick={handleResendRegistrationMailButtonClick}
            onSendResetPasswordAndPinButtonClick={handleSendResetPasswordAndPinButtonClick}
        />
    )
}

export default ConnectedUserDetail
